const MS_PER_MINUTE = 60 * 1000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;

export function formatWeatherTimestamp(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatRelativeTime(value) {
  if (!value) {
    return "No recent data";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Unknown time";
  }

  const diffMs = Date.now() - date.getTime();
  if (diffMs < MS_PER_MINUTE) {
    return "Just now";
  }

  if (diffMs < MS_PER_HOUR) {
    const minutes = Math.round(diffMs / MS_PER_MINUTE);
    return `${minutes} min ago`;
  }

  if (diffMs < 24 * MS_PER_HOUR) {
    const hours = Math.round(diffMs / MS_PER_HOUR);
    return `${hours} hr ago`;
  }

  return formatWeatherTimestamp(value);
}

export function getFreshnessStatus(observedAt) {
  if (!observedAt) {
    return { status: "stale", label: "No recent data", tone: "muted" };
  }

  const date = new Date(observedAt);
  if (Number.isNaN(date.getTime())) {
    return { status: "unknown", label: "Unknown freshness", tone: "muted" };
  }

  const ageMs = Date.now() - date.getTime();

  if (ageMs <= 30 * MS_PER_MINUTE) {
    return { status: "fresh", label: "Fresh", tone: "good" };
  }

  if (ageMs <= 2 * MS_PER_HOUR) {
    return { status: "recent", label: "Recent", tone: "ok" };
  }

  if (ageMs <= 24 * MS_PER_HOUR) {
    return { status: "aging", label: "Aging", tone: "warn" };
  }

  return { status: "stale", label: "Stale", tone: "muted" };
}

export function getRainfallCategory(rainfallMm) {
  if (rainfallMm == null || !Number.isFinite(Number(rainfallMm))) {
    return { id: "none", label: "No data", color: "#94a3b8" };
  }

  const value = Number(rainfallMm);

  if (value <= 0) {
    return { id: "none", label: "No rain", color: "#94a3b8" };
  }

  if (value < 2) {
    return { id: "light", label: "Light rain", color: "#bae6fd" };
  }

  if (value < 10) {
    return { id: "moderate", label: "Moderate rain", color: "#0284c7" };
  }

  if (value < 25) {
    return { id: "heavy", label: "Heavy rain", color: "#f97316" };
  }

  return { id: "extreme", label: "Extreme rain", color: "#ef4444" };
}

export function normalizeRiskLevel(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();

  if (["normal", "ok", "bajo", "low"].includes(normalized)) {
    return "normal";
  }

  if (["elevated", "moderate", "moderado", "watch", "yellow"].includes(normalized)) {
    return "elevated";
  }

  if (["high", "alto", "warning", "orange"].includes(normalized)) {
    return "high";
  }

  if (["critical", "extreme", "overflow", "emergency", "red", "critico"].includes(normalized)) {
    return "critical";
  }

  return "unknown";
}

export function getRiskStyle(riskLevel) {
  switch (riskLevel) {
    case "normal":
      return { label: "Normal", color: "#3b82f6" };
    case "elevated":
      return { label: "Elevated", color: "#eab308" };
    case "high":
      return { label: "High risk", color: "#f97316" };
    case "critical":
      return { label: "Critical", color: "#ef4444" };
    default:
      return { label: "No data", color: "#94a3b8" };
  }
}

export function normalizeAlertSeverity(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();

  if (["info", "information", "informacion"].includes(normalized)) {
    return "info";
  }

  if (["watch", "vigilancia"].includes(normalized)) {
    return "watch";
  }

  if (["warning", "alerta", "moderate"].includes(normalized)) {
    return "warning";
  }

  if (["emergency", "emergencia", "extreme", "critical"].includes(normalized)) {
    return "emergency";
  }

  return normalized || "info";
}

export function getAlertSeverityStyle(severity) {
  switch (normalizeAlertSeverity(severity)) {
    case "watch":
      return { label: "Watch", color: "#eab308" };
    case "warning":
      return { label: "Warning", color: "#f97316" };
    case "emergency":
      return { label: "Emergency", color: "#ef4444" };
    default:
      return { label: "Info", color: "#3b82f6" };
  }
}

export function parseNumeric(value) {
  if (value == null || value === "") {
    return null;
  }

  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}
